import {
  FEDERAL_COURT_GROUPS,
  FEDERAL_DISTRICT_KEYS_BY_STATE,
  FEDERAL_DISTRICT_LABELS,
  FEDERAL_DISTRICT_TO_COURTS_MAP,
  FEDERAL_LRR_SOURCES,
  STANDARD_FEDERAL_COURTS,
  STATE_LABELS,
  STATE_LRR_SOURCES_MAP,
  STATE_TO_STATE_COURT_MAP,
  SUPREME_FEDERAL_COURTS,
} from './legal-research-sources.generated'

export { FEDERAL_DISTRICT_LABELS, FEDERAL_DISTRICT_TO_COURTS_MAP, STATE_LABELS, STATE_LRR_SOURCES_MAP, STATE_TO_STATE_COURT_MAP }

const FEDERAL_COURT_CODES_SET = new Set(FEDERAL_COURT_GROUPS.flatMap((group) => group.courts.flatMap((court) => court.codes)))

export function getJurisdictionLabel(jurisdiction: string): string {
  return STATE_LABELS[jurisdiction] || FEDERAL_DISTRICT_LABELS[jurisdiction] || jurisdiction
}

/**
 * Retrieves federal court IDs based on the provided state name.
 *
 * @param state - The name of the state.
 * @returns A list of federal court IDs.
 */
export function getFederalCourtIdsByState(state: string): string[] {
  const districtKeys = FEDERAL_DISTRICT_KEYS_BY_STATE[state] ?? []
  return districtKeys.flatMap((courtId) => FEDERAL_DISTRICT_TO_COURTS_MAP[courtId] || [])
}

/**
 * Retrieves standard federal court IDs (ones applicable to any state).
 *
 * @returns A list of standard federal court IDs.
 */
export function getStandardFederalCourtIds(): string[] {
  return STANDARD_FEDERAL_COURTS
}

/**
 * Retrieves standard federal LRR sources (ones which accompany any federal court and are applicable to any state).
 *
 * @returns A list of standard federal LRR sources.
 */
export function getStandardFederalLRRSources(): string[] {
  return FEDERAL_LRR_SOURCES
}

/**
 * Determines if a jurisdiction code is a federal court code.
 *
 * @param code - The jurisdiction code to check.
 * @returns True if the code represents a federal court, false otherwise.
 */
export function isFederalCourtCode(code: string): boolean {
  return FEDERAL_COURT_CODES_SET.has(code)
}

/**
 * Gets federal content related to a state, separated into courts and LRR sources.
 * Includes state-specific federal courts, standard federal courts, and federal LRR sources.
 *
 * @param stateName - The name of the state.
 * @returns Object containing arrays of courts and lrrSources.
 */
export function getRelatedFederalContent(stateName: string): {
  courts: string[]
  lrrSources: string[]
} {
  return {
    courts: [...getFederalCourtIdsByState(stateName), ...STANDARD_FEDERAL_COURTS],
    lrrSources: FEDERAL_LRR_SOURCES,
  }
}

export function getStateLrrSources(state: string | null): string[] {
  if (!state) return []
  return STATE_LRR_SOURCES_MAP[state.toLowerCase()] || []
}

export function getStateCaselawSources(state: string | null): string[] {
  if (!state) return []
  return STATE_TO_STATE_COURT_MAP[state.toLowerCase()] || []
}

export function isRelatedFederalContent(code: string, stateName: string | null): { isRelatedLrr: boolean; isRelatedCaselaw: boolean } {
  if (!stateName) return { isRelatedLrr: false, isRelatedCaselaw: false }

  const { courts, lrrSources } = getRelatedFederalContent(stateName)

  return {
    isRelatedLrr: lrrSources.includes(code),
    isRelatedCaselaw: courts.includes(code),
  }
}

export type ResearchSourcesFromJurisdiction = {
  selectedStateNames: string[]
  stateLrrSources: string[]
  stateCaselawSources: string[]
  federalLrrSources: string[]
  federalCaselawSources: string[]
}

/**
 * Infers whether a stored jurisdiction value is a state or federal district key.
 */
export function inferJurisdictionKind(jurisdiction: string | null | undefined): 'state' | 'federal' | null {
  if (!jurisdiction) return null
  if (Object.prototype.hasOwnProperty.call(STATE_TO_STATE_COURT_MAP, jurisdiction)) return 'state'
  if (Object.prototype.hasOwnProperty.call(FEDERAL_DISTRICT_TO_COURTS_MAP, jurisdiction)) return 'federal'
  return null
}

/**
 * Builds research filter buckets for chat prefill from a matter jurisdiction value.
 * State jurisdictions select state sources only. Federal districts select district courts + U.S.
 */
export function getResearchSourcesForJurisdiction(jurisdiction: string): ResearchSourcesFromJurisdiction {
  const kind = inferJurisdictionKind(jurisdiction)

  if (kind === 'state') {
    return {
      selectedStateNames: [jurisdiction],
      stateLrrSources: STATE_LRR_SOURCES_MAP[jurisdiction] || [],
      stateCaselawSources: STATE_TO_STATE_COURT_MAP[jurisdiction] || [],
      federalLrrSources: [],
      federalCaselawSources: [],
    }
  }

  if (kind === 'federal') {
    const districtCourts = FEDERAL_DISTRICT_TO_COURTS_MAP[jurisdiction] || []
    return {
      selectedStateNames: [],
      stateLrrSources: [],
      stateCaselawSources: [],
      federalLrrSources: [],
      federalCaselawSources: Array.from(new Set([...SUPREME_FEDERAL_COURTS, ...districtCourts])),
    }
  }

  return {
    selectedStateNames: [],
    stateLrrSources: [],
    stateCaselawSources: [],
    federalLrrSources: [],
    federalCaselawSources: [],
  }
}
